export default class OnboardingTour extends HTMLElement {
  static TOTAL_STEPS = 5

  connectedCallback () {
    this.stepData = JSON.parse(this.dataset.steps || '[]')
    this.i18n = JSON.parse(this.dataset.i18n || '{}')
    this.startStep = parseInt(this.dataset.startStep || '0', 10)
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ''

    // Mobile skip — same as existing app_tour.js
    if (window.innerWidth < 768) return

    this.onRestart = () => this.restart()
    window.addEventListener('doccu:restart-tour', this.onRestart)

    // Auto-start only if user has a saved step (1-5) — fresh users wait for
    // the "Start Tour" button click from the welcome modal
    if (this.startStep >= 1 && this.startStep <= OnboardingTour.TOTAL_STEPS) {
      this.start()
    }
  }

  disconnectedCallback () {
    if (this.onRestart) {
      window.removeEventListener('doccu:restart-tour', this.onRestart)
    }
    if (this.driverObj) {
      this.driverObj.destroy()
      this.driverObj = null
    }
  }

  restart () {
    this.startStep = 1
    this.saveStep(1)
    this.start()
  }

  async start () {
    this.injectStyles()
    const [{ driver }] = await Promise.all([
      import('driver.js'),
      import('driver.js/dist/driver.css')
    ])

    const steps = this.buildSteps()
    if (steps.length === 0) return

    this.driverObj = driver({
      popoverClass: 'onboarding-tour-popover',
      showProgress: false,
      allowClose: false,
      overlayOpacity: 0.6,
      stagePadding: 4,
      stageRadius: 8,
      stageStyle: {
        border: '3px solid #01d680',
        borderRadius: '8px'
      },
      nextBtnText: this.i18n.next || 'Next',
      prevBtnText: this.i18n.back || 'Back',
      doneBtnText: this.i18n.done || 'Done',
      onPopoverRender: (popover, { config, state }) => {
        this.customizePopover(popover, state)
      },
      onNextClick: (element, step, { driver }) => {
        const currentIndex = driver.getActiveIndex()
        const nextStep = currentIndex + 2 // 1-based step number
        if (nextStep <= OnboardingTour.TOTAL_STEPS) {
          this.saveStep(nextStep)
          driver.moveNext()
        } else {
          this.completeTour()
        }
      },
      onPrevClick: (element, step, { driver }) => {
        const currentIndex = driver.getActiveIndex()
        const prevStep = currentIndex // 0-based index + 1 - 1 = currentIndex
        if (prevStep >= 1) {
          this.saveStep(prevStep)
          driver.movePrevious()
        }
      }
    })

    this.driverObj.setSteps(steps)
    // Resume from saved step (0-based index) or start from beginning
    const startIndex = Math.max(0, this.startStep - 1)
    this.driverObj.drive(startIndex)
  }

  injectStyles () {
    if (document.getElementById('doccu-onboarding-tour-styles')) return

    const style = document.createElement('style')
    style.id = 'doccu-onboarding-tour-styles'
    style.textContent = `
      .doccu-onboarding-popover {
        border-radius: 20px !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12) !important;
        padding: 20px !important;
        max-width: 320px !important;
        font-family: 'Raleway', sans-serif !important;
        background: #ffffff !important;
      }
      .doccu-tour-header {
        display: flex !important;
        justify-content: space-between !important;
        align-items: center !important;
        margin-bottom: 12px !important;
      }
      .doccu-tour-step-counter {
        color: #01d680 !important;
        font-family: 'Raleway', sans-serif !important;
        font-size: 11px !important;
        font-weight: 600 !important;
        text-transform: uppercase !important;
        letter-spacing: 0.5px !important;
      }
      .doccu-tour-skip {
        color: #888888 !important;
        font-family: 'Raleway', sans-serif !important;
        font-size: 11px !important;
        font-weight: 500 !important;
        background: transparent !important;
        border: none !important;
        cursor: pointer !important;
        padding: 2px 4px !important;
        line-height: 1 !important;
        transition: color 0.15s ease !important;
      }
      .doccu-tour-skip:hover {
        color: #043f42 !important;
      }
      .doccu-tour-dots {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 14px !important;
        margin-top: 16px !important;
        padding-top: 12px !important;
        border-top: 1px solid #f0f0f0 !important;
      }
      .doccu-tour-dot {
        width: 8px !important;
        height: 8px !important;
        border-radius: 50% !important;
        background-color: #e0e0e0 !important;
        display: inline-block !important;
        transition: background-color 0.2s ease, transform 0.2s ease !important;
      }
      .doccu-tour-dot.active {
        background-color: #043f42 !important;
        transform: scale(1.1) !important;
      }
    `
    document.head.appendChild(style)
  }

  buildSteps () {
    const stepDefs = [
      {
        element: '[data-tour="dashboard-overview"], [data-tour="dashboard-stats"]',
        popover: {
          title: this.i18n.step_1_title || 'Dashboard Overview',
          description: this.i18n.step_1_description || 'Monitor real-time cohort and folio signing progress, inquiries, and recent activity.',
          side: 'bottom',
          align: 'start'
        }
      },
      {
        element: '[data-label="Cohorts"]',
        popover: {
          title: this.i18n.step_2_title || 'Cohorts',
          description: this.i18n.step_2_description || 'Manage learner cohorts, track programme progress, and coordinate bulk agreements and signings.',
          side: 'right',
          align: 'start'
        }
      },
      {
        element: '[data-label="Folios"], [data-label="Documents"]',
        popover: {
          title: this.i18n.step_3_title || 'Folios',
          description: this.i18n.step_3_description || 'Manage ad-hoc documents, track folio progress, and handle individual signing workflows.',
          side: 'right',
          align: 'start'
        }
      },
      {
        element: '#notification-bell',
        popover: {
          title: this.i18n.step_4_title || 'Activity & Notifications',
          description: this.i18n.step_4_description || 'Real-time signing activity feed, audit alerts, and notification badge.',
          side: 'bottom',
          align: 'end'
        }
      },
      {
        element: '[data-label="Settings"]',
        popover: {
          title: this.i18n.step_5_title || 'Settings & Trust',
          description: this.i18n.step_5_description || 'Manage team access, roles, 2FA security, billing, and Trust Center.',
          side: 'right',
          align: 'start'
        }
      }
    ]

    return stepDefs.filter((step) => document.querySelector(step.element))
  }

  customizePopover (popover, state) {
    const popoverEl = popover.wrapper
    if (!popoverEl) return

    const currentStep = state.activeIndex + 1
    const totalSteps = OnboardingTour.TOTAL_STEPS

    // Add custom class for styling hook
    popoverEl.classList.add('doccu-onboarding-popover')

    // Find or create the header row (Step counter + Skip)
    let headerRow = popoverEl.querySelector('.doccu-tour-header')
    if (!headerRow) {
      headerRow = document.createElement('div')
      headerRow.className = 'doccu-tour-header'
      popoverEl.insertBefore(headerRow, popoverEl.firstChild)
    }
    headerRow.replaceChildren()
    const counterSpan = document.createElement('span')
    counterSpan.className = 'doccu-tour-step-counter'
    counterSpan.textContent = this.i18n.step_counter?.replace('%{current}', currentStep).replace('%{total}', totalSteps) || `Step ${currentStep} of ${totalSteps}`

    const skipBtn = document.createElement('button')
    skipBtn.type = 'button'
    skipBtn.className = 'doccu-tour-skip'
    skipBtn.setAttribute('data-action', 'skip')
    skipBtn.textContent = this.i18n.skip || 'Skip'
    skipBtn.style.all = 'unset'
    skipBtn.style.color = '#888888'
    skipBtn.style.fontFamily = "'Raleway', sans-serif"
    skipBtn.style.fontSize = '11px'
    skipBtn.style.fontWeight = '500'
    skipBtn.style.cursor = 'pointer'
    skipBtn.style.padding = '2px 4px'
    skipBtn.addEventListener('click', () => {
      this.skipTour()
    })

    headerRow.appendChild(counterSpan)
    headerRow.appendChild(skipBtn)

    // Add progress dots below the popover
    let dotsContainer = popoverEl.querySelector('.doccu-tour-dots')
    if (!dotsContainer) {
      dotsContainer = document.createElement('div')
      dotsContainer.className = 'doccu-tour-dots'
      popoverEl.appendChild(dotsContainer)
    }
    dotsContainer.replaceChildren()
    for (let i = 0; i < totalSteps; i++) {
      const dot = document.createElement('span')
      dot.className = `doccu-tour-dot${i + 1 === currentStep ? ' active' : ''}`
      dotsContainer.appendChild(dot)
    }

    // Style the Next button (dark teal)
    const nextBtn = popoverEl.querySelector('.driver-popover-next-btn')
    if (nextBtn) {
      nextBtn.style.background = '#043f42'
      nextBtn.style.color = '#ffffff'
      nextBtn.style.borderRadius = '6px'
      nextBtn.style.fontFamily = "'Raleway', sans-serif"
      nextBtn.style.fontSize = '12px'
      nextBtn.style.fontWeight = '600'
      nextBtn.style.padding = '6px 14px'
      nextBtn.style.border = 'none'
      nextBtn.style.cursor = 'pointer'
    }

    // Style the Back button (ghost)
    const prevBtn = popoverEl.querySelector('.driver-popover-prev-btn')
    if (prevBtn) {
      prevBtn.style.color = '#888888'
      prevBtn.style.fontFamily = "'Raleway', sans-serif"
      prevBtn.style.fontSize = '12px'
      prevBtn.style.background = 'transparent'
      prevBtn.style.border = 'none'
      prevBtn.style.cursor = 'pointer'
      prevBtn.style.padding = '6px 10px'
    }

    // Style title
    const titleEl = popoverEl.querySelector('.driver-popover-title')
    if (titleEl) {
      titleEl.style.fontFamily = "'Raleway', sans-serif"
      titleEl.style.fontSize = '14px'
      titleEl.style.fontWeight = '600'
      titleEl.style.color = '#043f42'
      titleEl.style.marginBottom = '8px'
    }

    // Style description
    const descEl = popoverEl.querySelector('.driver-popover-description')
    if (descEl) {
      descEl.style.fontFamily = "'Raleway', sans-serif"
      descEl.style.fontSize = '13px'
      descEl.style.color = '#444444'
      descEl.style.lineHeight = '1.5'
    }

    // Style the popover wrapper
    popoverEl.style.background = '#ffffff'
    popoverEl.style.borderRadius = '20px'
    popoverEl.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.12)'
    popoverEl.style.padding = '20px'
    popoverEl.style.maxWidth = '320px'
    popoverEl.style.fontFamily = "'Raleway', sans-serif"
  }

  async saveStep (stepNumber) {
    try {
      await fetch('/user_configs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          user_config: {
            key: 'onboarding_step',
            value: stepNumber
          }
        })
      })
    } catch (err) {
      console.error('Failed to save onboarding step:', err)
    }
  }

  async completeTour () {
    this.isCompleting = true

    // Trigger confetti
    this.triggerConfetti()

    // Save completion state
    try {
      await Promise.all([
        fetch('/user_configs', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify({
            user_config: {
              key: 'onboarding_completed',
              value: true
            }
          })
        }),
        fetch('/user_configs', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify({
            user_config: {
              key: 'onboarding_step',
              value: OnboardingTour.TOTAL_STEPS + 1
            }
          })
        })
      ])
    } catch (err) {
      console.error('Failed to save tour completion:', err)
    }

    // Destroy the tour
    if (this.driverObj) {
      this.driverObj.destroy()
      this.driverObj = null
    }
  }

  async skipTour () {
    this.isCompleting = true

    try {
      await Promise.all([
        fetch('/user_configs', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify({
            user_config: {
              key: 'onboarding_completed',
              value: true
            }
          })
        }),
        fetch('/user_configs', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify({
            user_config: {
              key: 'onboarding_step',
              value: OnboardingTour.TOTAL_STEPS + 1
            }
          })
        })
      ])
    } catch (err) {
      console.error('Failed to save skip state:', err)
    }

    if (this.driverObj) {
      this.driverObj.destroy()
      this.driverObj = null
    }
  }

  async triggerConfetti () {
    try {
      const confetti = (await import('canvas-confetti')).default

      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#01d680', '#043f42'],
        disableForReducedMotion: true
      })
    } catch (err) {
      console.error('Failed to trigger confetti:', err)
    }
  }
}
